import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { apiUrl } from "../lib/api";
import "../styles/Guestbook.css";

const EMPTY_FORM = {
  name: "",
  relationship: "",
  location: "",
  message: "",
  website: "",
};

function formatDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
}

export default function Guestbook() {
  const [entries, setEntries] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const canSubmit = useMemo(
    () => !busy && form.name.trim().length >= 2 && form.message.trim().length >= 10,
    [busy, form.name, form.message],
  );

  useEffect(() => {
    const controller = new AbortController();

    async function loadEntries() {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({
          page: String(page),
          pageSize: "5",
        });
        if (searchQuery) params.set("q", searchQuery);
        const response = await fetch(apiUrl(`/api/guestbook?${params.toString()}`), {
          headers: { Accept: "application/json" },
          cache: "no-store",
          signal: controller.signal,
        });
        const data = await response.json();
        if (!response.ok || data.ok === false) {
          throw new Error(data.error || "Could not load the guestbook");
        }
        setEntries(Array.isArray(data.entries) ? data.entries : []);
        setTotalPages(Math.max(1, Number(data.pagination?.totalPages || 1)));
        setTotalEntries(Math.max(0, Number(data.pagination?.total || 0)));
      } catch (err) {
        if (err.name !== "AbortError") setError(err.message || "Could not load the guestbook");
      } finally {
        setLoading(false);
      }
    }

    loadEntries();
    return () => controller.abort();
  }, [page, reloadKey, searchQuery]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  function updateField(event) {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  }

  function searchEntries(event) {
    event.preventDefault();
    setPage(1);
    setSearchQuery(searchInput.trim());
  }

  function clearSearch() {
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
  }

  async function submitEntry(event) {
    event.preventDefault();
    if (!canSubmit) return;

    setBusy(true);
    setError("");
    setSuccess("");

    try {
      const response = await fetch(apiUrl("/api/guestbook"), {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await response.json();
      if (!response.ok || data.ok === false) {
        throw new Error(data.error || "Could not save your entry");
      }

      setForm(EMPTY_FORM);
      setSuccess("Thank you. Your reflection is now published in the guestbook.");
      setPage(1);
      setReloadKey((value) => value + 1);
    } catch (err) {
      setError(err.message || "Could not save your entry");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="guestbook" aria-labelledby="guestbook-title">
      <header className="guestbook__header">
        <span className="guestbook__cross" aria-hidden="true">✝</span>
        <p className="guestbook__eyebrow">Reflections · Memories · Warm Wishes</p>
        <h1 id="guestbook-title">Guestbook</h1>
        <p className="guestbook__lead">
          A digital guestbook is open below for anyone who wishes to share personal
          reflections, favorite memories, or warm wishes for Father Paul.
        </p>
        <Link className="guestbook__admin-link" to="/guestbook/admin">
          Guestbook Admin
        </Link>
      </header>

      <div className="guestbook__layout">
        <form className="guestbook__form" onSubmit={submitEntry}>
          <h2>Sign the Guestbook</h2>
          <p className="guestbook__privacy">
            Your entry will appear publicly as soon as it is submitted. The family may
            remove inappropriate messages.
          </p>

          <input
            className="guestbook__honeypot"
            type="text"
            name="website"
            value={form.website}
            onChange={updateField}
            tabIndex="-1"
            autoComplete="off"
            aria-hidden="true"
          />

          <label>
            <span>Your name</span>
            <input
              name="name"
              value={form.name}
              onChange={updateField}
              maxLength={80}
              autoComplete="name"
              required
            />
          </label>

          <div className="guestbook__form-row">
            <label>
              <span>Relationship <small>(optional)</small></span>
              <input
                name="relationship"
                value={form.relationship}
                onChange={updateField}
                maxLength={100}
                placeholder="Friend, parishioner, family…"
              />
            </label>
            <label>
              <span>Location <small>(optional)</small></span>
              <input
                name="location"
                value={form.location}
                onChange={updateField}
                maxLength={100}
                autoComplete="address-level2"
                placeholder="City, country"
              />
            </label>
          </div>

          <label>
            <span>Your reflection or warm wishes</span>
            <textarea
              name="message"
              value={form.message}
              onChange={updateField}
              rows={7}
              minLength={10}
              maxLength={5000}
              required
            />
          </label>
          <div className="guestbook__form-footer">
            <span>{form.message.length}/5000</span>
            <button type="submit" disabled={!canSubmit}>
              {busy ? "Adding your reflection…" : "Add to Guestbook"}
            </button>
          </div>

          <div className="guestbook__status" aria-live="polite">
            {success && <p className="guestbook__success">{success}</p>}
            {error && <p className="guestbook__error">{error}</p>}
          </div>
        </form>

        <div className="guestbook__entries" aria-live="polite">
          <h2>Messages for Father Paul</h2>
          <form className="guestbook__search" role="search" onSubmit={searchEntries}>
            <label htmlFor="guestbook-search">Search the Guestbook</label>
            <div className="guestbook__search-controls">
              <input
                id="guestbook-search"
                type="search"
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                maxLength={100}
                placeholder="Search names, places, relationships, or messages"
              />
              <button type="submit">Search</button>
              {searchQuery && (
                <button className="guestbook__search-clear" type="button" onClick={clearSearch}>
                  Clear
                </button>
              )}
            </div>
            {searchQuery && !loading && (
              <p>{totalEntries} {totalEntries === 1 ? "message" : "messages"} found for “{searchQuery}”</p>
            )}
          </form>
          {!loading && totalPages > 1 && (
            <div className="guestbook__pager guestbook__pager--top">
              <button
                type="button"
                onClick={() => setPage((value) => Math.max(1, value - 1))}
                disabled={page <= 1}
              >
                ← Previous
              </button>
              <button
                type="button"
                onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                disabled={page >= totalPages}
              >
                Next →
              </button>
              <span>Page {page} of {totalPages}</span>
            </div>
          )}
          {loading && <p className="guestbook__empty">Opening the guestbook…</p>}
          {!loading && entries.length === 0 && !error && (
            <p className="guestbook__empty">
              {searchQuery
                ? `No guestbook messages matched “${searchQuery}”.`
                : "Be the first to leave a reflection for Father Paul."}
            </p>
          )}
          {entries.map((entry) => (
            <article className="guestbook-entry" key={entry.id}>
              <blockquote>{entry.message}</blockquote>
              <footer>
                <strong>{entry.name}</strong>
                {(entry.relationship || entry.location) && (
                  <span>
                    {[entry.relationship, entry.location].filter(Boolean).join(" · ")}
                  </span>
                )}
                <time dateTime={entry.created_at}>{formatDate(entry.created_at)}</time>
              </footer>
            </article>
          ))}
          {!loading && totalPages > 1 && (
            <div className="guestbook__pager guestbook__pager--bottom">
              <div className="guestbook__pager-arrows">
                <button
                  type="button"
                  onClick={() => setPage((value) => Math.max(1, value - 1))}
                  disabled={page <= 1}
                >
                  ← Previous
                </button>
                <button
                  type="button"
                  onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
                  disabled={page >= totalPages}
                >
                  Next →
                </button>
              </div>
              <nav className="guestbook__pagination" aria-label="Guestbook pages">
                {paginationItems(page, totalPages).map((item, index) => (
                  item === "ellipsis" ? (
                    <span className="guestbook__pagination-ellipsis" key={`ellipsis-${index}`}>…</span>
                  ) : (
                    <button
                      type="button"
                      key={item}
                      className={item === page ? "active" : ""}
                      aria-current={item === page ? "page" : undefined}
                      onClick={() => setPage(item)}
                    >
                      {item}
                    </button>
                  )
                ))}
              </nav>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function paginationItems(currentPage, totalPages) {
  if (totalPages <= 9) return Array.from({ length: totalPages }, (_, index) => index + 1);

  const pages = new Set([1, 2, totalPages - 1, totalPages]);
  for (let page = currentPage - 2; page <= currentPage + 2; page += 1) {
    if (page > 0 && page <= totalPages) pages.add(page);
  }

  const ordered = [...pages].sort((a, b) => a - b);
  const items = [];
  ordered.forEach((page, index) => {
    if (index > 0 && page - ordered[index - 1] > 1) items.push("ellipsis");
    items.push(page);
  });
  return items;
}
